import React, { useState } from 'react'
import { MutationActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate'
import { Panel } from 'primereact/panel'
import { InputTextarea } from 'primereact/inputtextarea'

import { Button } from '../../../common/components/Button'
import DatePicker from '../../../common/components/DatePicker'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { IChargePoint } from '../../../common/hooks/useChargePoint'
import {
    AvailabilityType,
    useOcppCommandChangeAvailabilityMutation,
    useOcppCommandClearCacheMutation,
    useOcppCommandDataTransferMutation,
    useOcppCommandGetConfigurationMutation,
    useOcppCommandGetDiagnosticsMutation,
    useOcppCommandRemoteStartMutation,
    useOcppCommandRemoteStopMutation,
    useOcppCommandResetMutation,
    useOcppCommandTriggerMessageMutation,
    useOcppCommandUnlockConnectorMutation,
    useOcppCommandUpdateFirmwareMutation,
} from '../../../common/redux/api/web-api'
import { SFormRow, SLabelText } from '../../../common/styles/Form'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import formatDate from '../../../lib/formatDate'
import { ResetType } from '../../../lib/reset-ocpp-charge-point'
import ConfirmationModal from '../../../ui/ConfirmationModal'
import RowDivider from '../../../common/components/RowDivider'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'
import { Dropdown } from '../../../common/uikit/Dropdown'
import { Input } from '../../../common/uikit/Input'

import { ConnectorHeader, SButtonGroup } from './charge-point-ocpp-commands-widget.style'

export type ChargePointOcppCommandsWidgetProps = {
    chargePoint: IChargePoint
}

export enum OcppCommandTypesEnum {
    ChargerControls = 'ChargerControls',
    Connector = 'Connector Commands',
    GetDiagnostics = 'Diagnostics',
    UpdateFirmware = 'Update firmware',
    TriggerMessage = 'Trigger message',
}

export const OcppCommandoptions = [
    { label: 'Charger Controls', value: 'ChargerControls' },
    { label: 'Connector Commands', value: 'Connector' },
    { label: 'Get Diagnostics', value: 'GetDiagnostics' },
    { label: 'Update firmware', value: 'UpdateFirmware' },
    { label: 'Trigger Message', value: 'TriggerMessage' },
]

enum OperativityEnum {
    Inoperative = 'Inoperative',
    Operative = 'Operative',
}

export function ChargePointOcppCommandsWidget({ chargePoint }: ChargePointOcppCommandsWidgetProps): React.ReactElement {
    const text = useText()
    const [authToken, setAuthToken] = useState<string[]>(new Array(chargePoint.connectors.length))
    const [diagnosticsLocation, setDiagnosticsLocation] = useState<string | undefined>()
    const [fwUpdateRetryInterval, setFwUpdateRetryInterval] = useState<undefined | number>(undefined)
    const [fwUpdateRetries, setFwUpdateRetries] = useState<undefined | number>(undefined)
    // date-time input accepts and works with format YYYY-MM-DDThh:mm
    const [fwRetrieveDate, setFwRetrieveDate] = useState<string>(formatDate(new Date(), "yyyy-MM-dd'T'hh:mm"))

    const [requestedMessage, setRequestedMessage] = useState('')
    const [updateFWLocation, setUpdateFWLocation] = useState('')
    const [selectedOcppCommandType, setSelectedOcppCommandType] = useState<OcppCommandTypesEnum>(
        OcppCommandTypesEnum.ChargerControls
    )
    const [showHardResetModal, setShowHardResetModal] = useState<boolean>(false)
    const [transactionId, setTransactionId] = useState<number[]>(new Array(chargePoint.connectors.length))

    const [getDiagnosticsReq] = useOcppCommandGetDiagnosticsMutation()
    const diagnostics = () => {
        sendRequest(
            OcppCommandTypesEnum.GetDiagnostics,
            getDiagnosticsReq({
                chargePointId: chargePoint.id,
                getDiagnosticsReq: { location: diagnosticsLocation },
            })
        )
    }

    const [updateFirmwareReq] = useOcppCommandUpdateFirmwareMutation()
    const updateFw = () => {
        sendRequest(
            OcppCommandTypesEnum.UpdateFirmware,
            updateFirmwareReq({
                chargePointId: chargePoint.id,
                updateFirmwareReq: {
                    location: updateFWLocation,
                    retrieveDate: new Date(fwRetrieveDate).toISOString(),
                    ...(fwUpdateRetries && { retries: fwUpdateRetries }),
                    ...(fwUpdateRetryInterval && { retryInterval: fwUpdateRetryInterval }),
                },
            })
        )
    }

    const [unlockConnectorReq] = useOcppCommandUnlockConnectorMutation()
    const unlockConnector = async (connectorId: number) => {
        sendRequest(
            text('btnUnlockConnector'),
            unlockConnectorReq({
                chargePointId: chargePoint.id,
                unlockConnectorReq: { connectorId: connectorId },
            })
        )
    }

    const [remoteStartReq] = useOcppCommandRemoteStartMutation()
    const remoteStart = async (connectorId: number) => {
        sendRequest(
            text('btnRemoteStart'),
            remoteStartReq({
                chargePointId: chargePoint.id,
                remoteStartTransactionReq: {
                    connectorId: connectorId,
                    idTag: authToken[connectorId - 1],
                },
            })
        )
    }

    const [remoteStopRequest] = useOcppCommandRemoteStopMutation()
    const remoteStop = (connectorId: number) => {
        sendRequest(
            text('btnRemoteStop'),
            remoteStopRequest({
                chargePointId: chargePoint.id,
                remoteStopTransactionReq: {
                    transactionId: transactionId[connectorId - 1],
                },
            })
        )
    }

    const [triggerMessageRequest] = useOcppCommandTriggerMessageMutation()
    const sendTriggerMessage = () => {
        sendRequest(
            text('btnSendTriggerMessage'),
            triggerMessageRequest({
                chargePointId: chargePoint.id,
                triggerMessageReq: {
                    requestedMessage,
                },
            })
        )
    }

    const [resetRequest] = useOcppCommandResetMutation()
    const reset = (resetType: ResetType) => {
        sendRequest(
            text('msgResetChargePoint'),
            resetRequest({
                chargePointId: chargePoint.id,
                resetReq: {
                    type: resetType,
                },
            })
        )
    }

    const [dataTransferRequest] = useOcppCommandDataTransferMutation()
    const dataTransfer = (messageName: string, messageId: string) => {
        sendRequest(
            messageName,

            dataTransferRequest({
                chargePointId: chargePoint.id,
                dataTransferReq: {
                    vendorId: 'com.chargeamps',
                    messageId,
                },
            })
        )
    }

    const [clearCacheRequest] = useOcppCommandClearCacheMutation()
    const clearCache = () => {
        sendRequest(
            text('btnClearCache'),
            clearCacheRequest({
                chargePointId: chargePoint.id,
            })
        )
    }

    const [getConfigurationRequest] = useOcppCommandGetConfigurationMutation()
    const getConfiguration = () => {
        sendRequest(
            text('btnGetConfig'),
            getConfigurationRequest({
                chargePointId: chargePoint.id,
                getConfigurationReq: {},
            })
        )
    }

    const [setAvailabilityRequest] = useOcppCommandChangeAvailabilityMutation()
    const setAvailability = (connector: number, type: AvailabilityType) => {
        sendRequest(
            `${text('lblInoperativeOrOperativePlaceholder')} ${type}`,
            setAvailabilityRequest({
                chargePointId: chargePoint.id,
                changeAvailabilityReq: {
                    connectorId: connector,
                    type,
                },
            })
        )
    }

    const sendRequest = async (requestType: string, request: MutationActionCreatorResult<T>) => {
        try {
            await request.unwrap()
            notifySuccess(
                text('lblOcppCommandSent', {
                    commandName: requestType,
                })
            )
        } catch (exception) {
            notifyError(text('lblOcppCommandSendError', { commandName: requestType }))
        }
    }

    const connectorCommands = (connectorName: string, isChargingConnector: boolean, key: number) => {
        const connectorNumber = key + 1
        return (
            <>
                {chargePoint.connectors.length > 1 && <ConnectorHeader>{connectorName}</ConnectorHeader>}
                {isChargingConnector && (
                    <div>
                        <SLabelText>{text('lblRemoteStartStop')}</SLabelText>
                        <SButtonGroup>
                            <Input
                                id="auth-token-input"
                                data-testid={`auth-token-input-${key}`}
                                label={text('lblAuthTokenPlaceholder')}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const authTokenClone: string[] = [...authToken]
                                    authTokenClone[key] = e.target.value
                                    setAuthToken(authTokenClone)
                                }}
                                value={authToken[key]}
                            />
                        </SButtonGroup>
                        <SButtonGroup>
                            <Button
                                onClick={() => remoteStart(connectorNumber)}
                                label={text('btnRemoteStart')}
                                data-testid={`remote-start-btn-${key}`}
                                size="sm"
                                disabled={!(authToken?.length && authToken[key])}
                            />
                        </SButtonGroup>
                        <SButtonGroup>
                            <Input
                                id="transaction-id-input"
                                data-testid={`transaction-id-input-${key}`}
                                label={text('lblTransactionIdPlaceholder')}
                                type="number"
                                min={0}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const transactionIdClone: number[] = [...transactionId]
                                    transactionIdClone[key] = Number(e.target.value)
                                    setTransactionId(transactionIdClone)
                                }}
                                value={transactionId[key]}
                            />
                        </SButtonGroup>
                        <SButtonGroup>
                            <Button
                                onClick={() => remoteStop(connectorNumber)}
                                label={text('btnRemoteStop')}
                                data-testid={`remote-stop-btn-${key}`}
                                size="sm"
                                disabled={!(transactionId?.length && transactionId[key])}
                            />
                        </SButtonGroup>
                    </div>
                )}
                <div>
                    <SLabelText>{text('lblSetAvailability')} </SLabelText>
                    <SButtonGroup>
                        <Button
                            onClick={() => setAvailability(connectorNumber, OperativityEnum.Inoperative)}
                            data-testid={`set-inoperative-btn-${key}`}
                            label={text('btnInoperative')}
                            size="sm"
                        />
                        <Button
                            onClick={() => setAvailability(connectorNumber, OperativityEnum.Operative)}
                            data-testid={`set-operative-btn-${key}`}
                            label={text('btnOperative')}
                            size="sm"
                        />
                    </SButtonGroup>
                </div>
                {isChargingConnector && (
                    <div>
                        <SLabelText>{text('lblConnectorLock')}</SLabelText>
                        <SButtonGroup>
                            <Button
                                onClick={() => unlockConnector(connectorNumber)}
                                data-testid={`unlock-connector-btn-${key}`}
                                label={text('btnUnlock')}
                                size="sm"
                            />
                        </SButtonGroup>
                    </div>
                )}
            </>
        )
    }

    return (
        <Panel header={text('lblOcppCommandsWidget')} data-testid="ocpp-commands-widget">
            <div className="ml-2 mr-2">
                <form>
                    <Dropdown
                        options={OcppCommandoptions.map((option) => ({ value: option.value, label: option.label }))}
                        id="ocpp-command"
                        data-testid="ocpp-command-select"
                        onChange={(event) => setSelectedOcppCommandType(event.value)}
                        value={selectedOcppCommandType}
                    />

                    <div className="mt-3 text-lg">
                        <div className="font-medium">{text('lblAvailableCommands')}</div>
                    </div>
                    <div>
                        <SFormRow>
                            {(OcppCommandTypesEnum as unknown)[selectedOcppCommandType] ===
                            OcppCommandTypesEnum.GetDiagnostics ? (
                                <>
                                    <Input
                                        id="diagnostics-url"
                                        data-testid="diagnostics-url-input"
                                        label={text('lblDiagnosticsLocationPlaceholder')}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setDiagnosticsLocation(e.target.value)
                                        }
                                        value={diagnosticsLocation}
                                    />
                                    <SButtonGroup>
                                        <Button
                                            data-testid="get-diagnostics-btn"
                                            onClick={diagnostics}
                                            label={text('btnGetDiagnostics')}
                                            size="sm"
                                            disabled={!diagnosticsLocation?.length}
                                        />
                                    </SButtonGroup>
                                </>
                            ) : null}
                            {(OcppCommandTypesEnum as unknown)[selectedOcppCommandType] ===
                            OcppCommandTypesEnum.UpdateFirmware ? (
                                <>
                                    <Input
                                        id="firmware-url"
                                        data-testid="firmware-url-input"
                                        label={text('lblFirmwareUploadPlaceholder')}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setUpdateFWLocation(e.target.value)
                                        }
                                        value={updateFWLocation}
                                    />

                                    <SLabelText>{text('lblRetrievalOptions')}</SLabelText>
                                    <SButtonGroup>
                                        <div className="flex flex-column w-9">
                                            <span>Retrieval date</span>
                                            <DatePicker
                                                id={'retrieve-date'}
                                                type="datetime-local"
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setFwRetrieveDate(event.target.value)
                                                }}
                                                value={fwRetrieveDate}
                                            />
                                        </div>

                                        <div className="flex w-6 gap-2">
                                            <Input
                                                min={0}
                                                id="Retries"
                                                type="number"
                                                label="Retries"
                                                name="retries"
                                                className="text-right"
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                    setFwUpdateRetries(
                                                        event.target.value ? Number(event.target.value) : undefined
                                                    )
                                                }
                                                value={fwUpdateRetries ?? ''}
                                            />
                                            <Input
                                                min={0}
                                                id="RetryInterval"
                                                label="Retry interval"
                                                type="number"
                                                name="retryInterval"
                                                className="text-right"
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                    setFwUpdateRetryInterval(
                                                        event.target.value ? Number(event.target.value) : undefined
                                                    )
                                                }
                                                value={fwUpdateRetryInterval ?? ''}
                                            />
                                        </div>
                                    </SButtonGroup>
                                    <SButtonGroup>
                                        <Button
                                            data-testid="update-firmware-btn"
                                            onClick={updateFw}
                                            label={text('btnUpdateFirmware')}
                                            size="sm"
                                            disabled={!updateFWLocation.length}
                                        />
                                    </SButtonGroup>
                                </>
                            ) : null}
                            {(OcppCommandTypesEnum as unknown)[selectedOcppCommandType] ===
                            OcppCommandTypesEnum.TriggerMessage ? (
                                <>
                                    <InputTextarea
                                        onChange={(event) => setRequestedMessage(event.target.value)}
                                        data-testid="trigger-message-textarea"
                                        value={requestedMessage}
                                        placeholder={text('lblPayloadPlaceholder')}
                                        rows={5}
                                        cols={85}
                                    />
                                    <SButtonGroup>
                                        <Button
                                            onClick={sendTriggerMessage}
                                            label={text('btnSendTriggerMessage')}
                                            size="sm"
                                            disabled={!requestedMessage.length}
                                            data-testid="trigger-message-btn"
                                        />
                                    </SButtonGroup>
                                </>
                            ) : null}
                            {(OcppCommandTypesEnum as unknown)[selectedOcppCommandType] ===
                                OcppCommandTypesEnum.Connector &&
                                chargePoint.connectors.map((connector, index) => (
                                    <div key={`connector-${connector.connectorId}`}>
                                        {connectorCommands(connector.name, isChargingConnector(connector), index)}
                                        {index + 1 < chargePoint.connectors.length &&
                                            chargePoint.connectors.length > 1 && <hr key={`hr-${index}`} />}
                                    </div>
                                ))}
                            {(OcppCommandTypesEnum as unknown)[selectedOcppCommandType] ===
                            OcppCommandTypesEnum.ChargerControls ? (
                                <>
                                    <SLabelText>{text('lblDataTransfer')}</SLabelText>
                                    <div>
                                        <SButtonGroup>
                                            <Button
                                                data-testid="component-info-btn"
                                                onClick={() =>
                                                    dataTransfer(text('btnComponentInformation'), 'componentInfo')
                                                }
                                                label={text('btnComponentInformation')}
                                                size="sm"
                                            />
                                            <Button
                                                data-testid="network-status-btn"
                                                onClick={() => dataTransfer(text('btnNetworkStatus'), `networkStatus`)}
                                                label={text('btnNetworkStatus')}
                                                size="sm"
                                            />
                                        </SButtonGroup>
                                    </div>
                                    <RowDivider />
                                    <SLabelText>{text('lblGeneralCommands')}</SLabelText>
                                    <div>
                                        <SButtonGroup>
                                            <Button
                                                data-testid="clear-cache-btn"
                                                onClick={clearCache}
                                                label={text('btnClearCache')}
                                                size="sm"
                                            />
                                            <Button
                                                data-testid="get-config-btn"
                                                onClick={getConfiguration}
                                                label={text('btnGetConfig')}
                                                size="sm"
                                            />
                                        </SButtonGroup>
                                    </div>
                                    <RowDivider />
                                    <SLabelText>{text('lblChargerReset')}</SLabelText>
                                    <SButtonGroup>
                                        <Button
                                            data-testid="reset-hard-btn"
                                            onClick={() => setShowHardResetModal(true)}
                                            label={text('btnResetHard')}
                                            size="sm"
                                        />
                                        <Button
                                            data-testid="reset-soft-btn"
                                            onClick={() => reset(ResetType.SOFT)}
                                            label={text('btnResetSoft')}
                                            size="sm"
                                        />
                                    </SButtonGroup>
                                </>
                            ) : null}
                        </SFormRow>
                    </div>
                </form>
            </div>
            {showHardResetModal && (
                <ConfirmationModal
                    message={<Text code="msgHardResetLabel" />}
                    onConfirm={() => {
                        reset(ResetType.HARD)
                        setShowHardResetModal(false)
                    }}
                    onClose={() => setShowHardResetModal(false)}
                    confirmButtonText={text('btnResetHard')}
                />
            )}
        </Panel>
    )
}
